import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const GMDSS_DATE_FORMATS = ["DD MMM YYYY"] as const;

/** Returns the detected format if the value is a valid GMDSS expiry date string, otherwise null. */
export const getGmdssExpiryFormat = (value: string): string | null => {
  for (const fmt of GMDSS_DATE_FORMATS) {
    if (dayjs(value, fmt, true).isValid()) return fmt;
  }
  return null;
};

/** Returns true if the value looks like a GMDSS expiry date. */
export const isGmdssExpiryDate = (value: string): boolean =>
  getGmdssExpiryFormat(value) !== null;

/** Normalises a GMDSS expiry date string to DD MMM YYYY. Returns the original value if not parseable. */
export const formatGmdssExpiry = (value: string): string => {
  const fmt = getGmdssExpiryFormat(value);
  return fmt ? dayjs(value, fmt, true).format("DD MMM YYYY") : value;
};

/** Parses a GMDSS expiry date string to a Date object, or null if not parseable. */
export const parseGmdssExpiry = (value: string): Date | null => {
  const fmt = getGmdssExpiryFormat(value);
  return fmt ? dayjs(value, fmt, true).toDate() : null;
};

/** Builds the select options for a radioSelector cell, formatting expiry dates when isExp is true. */
export const buildRadioSelectorData = (
  items: Array<{ serial: string; exp: string }>,
  isExp: boolean,
): Array<{ value: string; label: string }> =>
  items
    .filter(item => (isExp ? item.exp : item.serial))
    .map(item => {
      const raw = isExp ? item.exp : item.serial;
      const label = isExp ? formatGmdssExpiry(raw) : raw;
      return { value: raw, label };
    });

/**
 * Finds the UUID of the radioSelector cell in the same column of another row
 * that targets the opposite field (serial vs expiry) for the same radioType.
 */
export const findRadioSiblingUuid = (
  rows: Array<{ cols?: Record<string, any> }> | undefined,
  rowIndex: number,
  colIndex: number,
  radioType: string,
  isExp: boolean,
): string | undefined =>
  rows
    ?.filter((_, idx) => idx !== rowIndex)
    .map(row => Object.entries(row.cols ?? {})[colIndex])
    .find(
      (entry): entry is [string, any] =>
        !!entry &&
        entry[1]?.radioType === radioType &&
        entry[1]?.exp !== isExp &&
        entry[1]?.unit === "radioSelector",
    )?.[0];
